'use client';

export default function WhyUnifyMax() {
  const withoutItems = [
    'Knowledge lives in one system, agents work in another. Errors compound.',
    'Information is siloed, teams are out of sync',
    'AI runs in isolation with no feedback loop to improve over time',
    'Performance is measured too late, after damage is already done',
    'Insights are isolated and delayed',
    'Each new channel adds complexity, not capability',
  ];

  const withItems = [
    'Every interaction feeds a shared knowledge base that continuously improves',
    'AI and human agents work in sync. Escalations are seamless',
    'Real-time compliance and performance visibility across all channels',
    'Physical data powers digital decisions through unified analytics',
    'Each new touchpoint strengthens the whole system',
    'When systems work together, growth becomes inevitable',
  ];

  return (
    <section className="py-20 bg-white">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Top Label */}
        <p
          className="text-xs font-semibold uppercase tracking-widest mb-6"
          style={{ color: 'var(--color-accent)', fontFamily: "'Titillium Web', sans-serif" }}
        >
          The Challenge
        </p>

        {/* Main Heading */}
        <h2
          className="text-4xl sm:text-5xl font-bold leading-tight mb-8 max-w-3xl"
          style={{ color: '#000', fontFamily: "'Titillium Web', sans-serif" }}
        >
          Most businesses operate with disconnected tools.
        </h2>

        {/* Description Paragraph */}
        <p
          className="text-base sm:text-lg leading-relaxed mb-12 max-w-2xl"
          style={{ color: 'var(--color-muted-foreground)' }}
        >
          Knowledge lives in one place, agents work in another. Information is siloed, teams are out of sync. AI runs in isolation, performance is measured too late. Insights are isolated and delayed. The result? Lost opportunities, inconsistent engagement, and growth that doesn't compound.
        </p>

        {/* Comparison Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          {/* Left Card: WITHOUT UNIFYMAX */}
          <div
            className="rounded-xl p-6 shadow-sm"
            style={{
              background: 'var(--color-card)',
              border: '1px solid var(--color-border)',
              borderTop: '3px solid var(--color-error)',
            }}
          >
            <p
              className="text-xs font-semibold uppercase tracking-widest mb-3"
              style={{ color: 'var(--color-error)' }}
            >
              ✕ Without UnifyMax
            </p>
            <h3
              className="text-lg font-bold mb-5"
              style={{ color: '#000', fontFamily: "'Titillium Web', sans-serif" }}
            >
              Fragmented tools, fragmented outcomes
            </h3>
            <ul className="space-y-3">
              {withoutItems?.map((item, i) => (
                <li key={i} className="flex items-start gap-3">
                  <span
                    className="mt-0.5 flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold"
                    style={{
                      background: '#FEE2E2',
                      color: 'var(--color-error)',
                    }}
                  >
                    ✕
                  </span>
                  <span
                    className="text-sm leading-relaxed"
                    style={{ color: 'var(--color-muted-foreground)' }}
                  >
                    {item}
                  </span>
                </li>
              ))}
            </ul>
          </div>

          {/* Right Card: WITH UNIFYMAX */}
          <div
            className="rounded-xl p-6 shadow-sm"
            style={{
              background: 'var(--color-card)',
              border: '1px solid var(--color-border)',
              borderTop: '3px solid var(--color-success)',
            }}
          >
            <p
              className="text-xs font-semibold uppercase tracking-widest mb-3"
              style={{ color: 'var(--color-success)' }}
            >
              ✓ With UnifyMax
            </p>
            <h3
              className="text-lg font-bold mb-5"
              style={{ color: '#000', fontFamily: "'Titillium Web', sans-serif" }}
            >
              One intelligent system, compounding growth
            </h3>
            <ul className="space-y-3">
              {withItems?.map((item, i) => (
                <li key={i} className="flex items-start gap-3">
                  <span
                    className="mt-0.5 flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold"
                    style={{
                      background: '#D1FAE5',
                      color: 'var(--color-success)',
                    }}
                  >
                    ✓
                  </span>
                  <span
                    className="text-sm leading-relaxed"
                    style={{ color: 'var(--color-muted-foreground)' }}
                  >
                    {item}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Bottom Highlight CTA Box */}
        <div
          className="rounded-xl px-8 py-7 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-5 bg-black"
          style={{ background: 'var(--color-surface)' }}
        >
          <p
            className="text-base sm:text-lg leading-relaxed max-w-2xl text-[rgba(242,242,242,1)]"
            style={{ color: 'var(--color-foreground)' }}
          >
            <strong>Great CX is no longer about tools.</strong> It's orchestrated through a single, intelligent system that eliminates silos, turning every interaction into a driver of revenue growth, higher CLV, and repeat purchases.
          </p>
          <a
            href="#"
            className="flex-shrink-0 inline-flex items-center gap-2 px-6 py-3 rounded-lg font-semibold text-sm transition-opacity hover:opacity-90 whitespace-nowrap"
            style={{
              background: 'var(--color-accent)',
              color: 'var(--color-primary-foreground)',
              fontFamily: "'Manrope', sans-serif",
            }}
          >
            See How It Works →
          </a>
        </div>
      </div>
    </section>
  );
}
